import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { v4 as uuidv4 } from 'uuid';
import styles from './styles.module.scss';

// Types
import { FormData, ProductType, ConsentState, SingleAnswerSlide, FormInputSlide } from './types';

// Hooks
import { useSliderNavigation, useZipCodeCheck, useSavingsCalculator } from './hooks';

// Components
import ProgressBar from './components/ProgressBar';
import SlideHeader from './components/SlideHeader';
import ChoiceGrid from './components/ChoiceGrid';
import FormInput from './components/FormInput';
import SuccessSlide from './components/SuccessSlide';
import {
  DEFAULT_OPTIONAL_CONSENTS,
  OptionalConsentAnswers,
} from '../../shared/consent/OptionalConsents';
import CerConsentSlide from './components/CerConsentSlide';

// Utils
import { submitLead } from './utils/api';
import {
  trackSlideAnswer,
  trackSliderSuccess,
  trackSliderFailure,
  trackLeadCreated,
  trackEnpalSourceCookie,
} from './utils/tracking';
import { getUTMParams, splitFullName } from './utils/helpers';

/** Slide con header beneficio senza riga cumulativa "Risparmio totale" nell'header */
const SLIDE_IDS_WITHOUT_HEADER_TOTAL: ReadonlySet<string> = new Set([
  'decision_stage',
  'name_form',
  'email_form',
  'phone_form',
]);

/**
 * SliderVpp — copia di slider_new_retrofit con bonus CAP dinamici (short_cap) e banner CAP (all_bonus).
 */
const SliderVpp: React.FC = () => {
  const [formData, setFormData] = useState<FormData>({});
  const [formValues, setFormValues] = useState<Record<string, string>>({});
  const [cerCompleted, setCerCompleted] = useState(false);

  const [mongoId, setMongoId] = useState<string>('');

  const [optionalConsents, setOptionalConsents] = useState<OptionalConsentAnswers>(
    DEFAULT_OPTIONAL_CONSENTS
  );

  const consentForApi: ConsentState = useMemo(
    () => ({
      enpalMarketingConsent: optionalConsents.enpalMarketingConsent === true,
      thirdPartyMarketing: optionalConsents.thirdPartyMarketing === true,
      thirdPartyProfiling: optionalConsents.thirdPartyProfiling === true,
    }),
    [optionalConsents]
  );

  const {
    currentSlide,
    progress,
    goNext,
    goPrev,
    goToSlideById,
    canGoPrev,
  } = useSliderNavigation(formData);

  const { zipFlag, isValid: isCapValid, capFeedback, checkZip } = useZipCodeCheck();

  const { tooltipText, getBenefitText, getTotalSavings, getNextButtonText, getBonusCountText } =
    useSavingsCalculator(formData.product_type as ProductType, zipFlag, formData.zip);

  useEffect(() => {
    const utmParams = getUTMParams();
    trackEnpalSourceCookie(utmParams.utm_source || 'organic');
  }, []);

  const updateFormData = useCallback((key: string, value: string) => {
    setFormData((prev) => ({ ...prev, [key]: value }));
  }, []);

  const handleAnswerSelect = useCallback(
    (slide: SingleAnswerSlide, value: string) => {
      updateFormData(slide.apiFieldName, value);
      trackSlideAnswer(slide.id, slide.title || '', value);

      if (slide.autoAdvance) {
        setTimeout(() => goNext(), 200);
      }
    },
    [updateFormData, goNext]
  );

  const handleFormInputChange = useCallback(
    (name: string, value: string) => {
      setFormValues((prev) => ({ ...prev, [name]: value }));

      if (name === 'zip') {
        checkZip(value, formData.product_type as ProductType);
        updateFormData('zip', value);
      }
    },
    [checkZip, updateFormData, formData.product_type]
  );

  const handleLeadSubmit = useCallback(async () => {
    // VPP: send to Make webhook (no DB submission)
    if (formData.enpal_entry_type === 'energy_resale_vpp') {
      const utmParams = getUTMParams();
      const finalVpp: FormData = { ...formData, ...formValues };
      const { firstName, lastName } = splitFullName(finalVpp.name || '');

      const params = new URLSearchParams({
        nome: firstName,
        cognome: lastName,
        iban: '',
        cf: '',
        telefono: finalVpp.phone || '',
        email: finalVpp.email || '',
        channel: 'landing_Enpal',
        utm_source: utmParams.utm_source || '',
        utm_medium: utmParams.utm_medium || '',
        utm_campaign: utmParams.utm_campaign || '',
        utm_content: utmParams.utm_content || '',
      });

      try {
        await fetch(
          `https://hook.eu1.make.celonis.com/kj74ja4vets8p4ant1d5qgiyffx2315y?${params.toString()}`,
          { method: 'GET' }
        );
      } catch (err) {
        console.error('VPP webhook error:', err);
      }

      goToSlideById('success');
      return;
    }

    goToSlideById('loading');

    const uid = uuidv4();

    const finalFormData: FormData = {
      ...formData,
      ...formValues,
    };

    try {
      const id = await submitLead(finalFormData, uid, consentForApi);

      if (id) {
        setMongoId(id);
        trackSliderSuccess();

        if (finalFormData.email) {
          const { firstName, lastName } = splitFullName(finalFormData.name || '');
          setTimeout(() => {
            trackLeadCreated(
              finalFormData.email!,
              finalFormData.phone || '',
              finalFormData.zip || '',
              firstName,
              lastName
            );
          }, 120);
        }

        goToSlideById('success');
      } else {
        trackSliderFailure();
        goToSlideById('error');
      }
    } catch (error) {
      console.error('Error submitting lead:', error);
      trackSliderFailure();
      goToSlideById('error');
    }
  }, [formData, formValues, consentForApi, goToSlideById]);

  const handleFormSubmit = useCallback(
    async (slide: FormInputSlide) => {
      Object.entries(formValues).forEach(([key, value]) => {
        updateFormData(key, value);
      });

      trackSlideAnswer(slide.id, slide.title || '', 'continue_button');

      if (slide.id === 'phone_form' || slide.id === 'vpp_contact') {
        await handleLeadSubmit();
      } else {
        goNext();
      }
    },
    [formValues, updateFormData, goNext, handleLeadSubmit]
  );


  const handleRetry = useCallback(() => {
    goToSlideById('customer_type');
  }, [goToSlideById]);

  const getSubmitLabel = (slideId: string): string => {
    if (slideId === 'phone_form' || slideId === 'vpp_contact') {
      return 'Scopri il risparmio totale';
    }
    if (slideId === 'name_form' || slideId === 'email_form') {
      return getNextButtonText(slideId);
    }
    return 'Avanti';
  };

  const renderSlideContent = () => {
    if (!currentSlide) return null;

    switch (currentSlide.type) {
      case 'SINGLE_ANSWER': {
        const slide = currentSlide as SingleAnswerSlide;
        const benefitText = getBenefitText(slide.id);
        const bonusCountText = getBonusCountText(slide.id);

        return (
          <>
            {slide.header && (
              <SlideHeader
                savingsSubText={bonusCountText || slide.header.savingsSubText}
                savingsText={benefitText || slide.header.savingsText}
                tooltipText={tooltipText}
                zipCode={formData.zip}
                totalSavings={
                  SLIDE_IDS_WITHOUT_HEADER_TOTAL.has(slide.id)
                    ? undefined
                    : getTotalSavings(slide.id)
                }
              />
            )}

            <div className={styles.slideBody}>
              <h3 className={styles.wizardTitle}>
                {slide.id === 'customer_type' && !formData.enpal_entry_type
                  ? 'Per quale prodotto vuoi scoprire i bonus?'
                  : slide.title}
              </h3>

              <ChoiceGrid
                answers={slide.answers}
                selectedValue={formData[slide.apiFieldName]}
                onSelect={(value) => handleAnswerSelect(slide, value)}
                columns={
                  slide.id === 'has_existing_system' ||
                  slide.id === 'cer_battery' ||
                  slide.id === 'install_timeline' ||
                  slide.id === 'decision_stage' ||
                  slide.id === 'enpal_entry_type' ||
                  slide.id === 'cer_legal_type'
                    ? 2
                    : 3
                }
                variant={
                  slide.id === 'has_existing_system' || slide.id === 'cer_battery'
                    ? 'yesno'
                    : undefined
                }
              />

              {!slide.autoAdvance && (
                <div className={styles.buttonGroup}>
                  {canGoPrev && (
                    <button type="button" className={styles.btnPrev} onClick={goPrev}>
                      Indietro
                    </button>
                  )}
                  <button
                    type="button"
                    className={styles.btnNext}
                    onClick={goNext}
                    disabled={!formData[slide.apiFieldName]}
                  >
                    Avanti
                  </button>
                </div>
              )}
            </div>
          </>
        );
      }

      case 'FORM_INPUT': {
        const slide = currentSlide as FormInputSlide;
        const benefitText = getBenefitText(slide.id);
        const bonusCountText = getBonusCountText(slide.id);
        const isPhoneSlide = slide.id === 'phone_form';
        const isLocationSlide = slide.id === 'location_form';

        return (
          <>
            {slide.header && (
              <SlideHeader
                savingsSubText={bonusCountText || slide.header.savingsSubText}
                savingsText={benefitText || slide.header.savingsText}
                tooltipText={tooltipText}
                zipCode={formData.zip}
                totalSavings={
                  SLIDE_IDS_WITHOUT_HEADER_TOTAL.has(slide.id)
                    ? undefined
                    : getTotalSavings(slide.id)
                }
              />
            )}

            <div className={styles.slideBody}>
              {slide.title && <h3 className={styles.wizardTitle}>{slide.title}</h3>}
              {slide.subtitle && <p className={styles.slideSubtitle}>{slide.subtitle}</p>}

              <FormInput
                inputs={slide.inputs}
                values={formValues}
                onChange={handleFormInputChange}
                onSubmit={() => handleFormSubmit(slide)}
                submitLabel={getSubmitLabel(slide.id)}
                showBackButton={canGoPrev && !isPhoneSlide}
                onBack={goPrev}
                isPhoneSlide={isPhoneSlide}
                isLocationSlide={isLocationSlide}
                capFeedback={capFeedback}
                isCapValid={isCapValid}
                optionalConsents={isPhoneSlide ? optionalConsents : undefined}
                onOptionalConsentsChange={
                  isPhoneSlide ? setOptionalConsents : undefined
                }
              />
            </div>
          </>
        );
      }

      case 'INFO': {
        if (currentSlide.id === 'loading') {
          return (
            <div className={styles.slideBody}>
              <h3 className={styles.wizardTitle}>{currentSlide.title}</h3>
              <p>Attendere prego...</p>
              <img
                src="https://i.gifer.com/origin/34/34338d26023e5515f6cc8969aa027bca.gif"
                alt="Loading..."
                style={{ marginTop: '1rem', maxWidth: '80px', borderRadius: '10px' }}
              />
            </div>
          );
        }

        if (currentSlide.id === 'success') {
          if (formData.enpal_entry_type === 'energy_resale_vpp') {
            return (
              <div className={styles.slideBody}>
                <div className={styles.cerThankYou}>
                  <div className={styles.cerThankYouIcon}>✓</div>
                  <h3 className={styles.wizardTitle}>Richiesta ricevuta!</h3>
                  <p>
                    Controlla la tua email, ti invieremo a breve tutti i documenti per procedere
                    con l'attivazione gratuita.
                  </p>
                </div>
              </div>
            );
          }

          return (
            <SuccessSlide
              productType={(formData.product_type || 'Photovoltaic') as ProductType}
              zipFlag={zipFlag}
              zipCode={formData.zip}
              mongoId={mongoId}
              phone={formData.phone}
            />
          );
        }

        if (currentSlide.id === 'error') {
          return (
            <div className={styles.slideBody}>
              <h3 className={styles.wizardTitle}>{currentSlide.title}</h3>
              <p>Si è verificato un errore...</p>
              <button
                type="button"
                className={styles.btnNext}
                onClick={handleRetry}
                style={{ marginTop: '1rem', maxWidth: '200px' }}
              >
                Riprova
              </button>
            </div>
          );
        }

        if (currentSlide.id === 'cer_intro') {
          return (
            <div className={styles.slideBody}>
              {currentSlide.title && (
                <h3 className={styles.wizardTitle}>{currentSlide.title}</h3>
              )}
              {currentSlide.html && (
                <div
                  className={styles.cerHtmlContent}
                  dangerouslySetInnerHTML={{ __html: currentSlide.html }}
                />
              )}
              <div className={styles.buttonGroup}>
                <button type="button" className={styles.btnNext} onClick={goNext}>
                  Inizia l'adesione
                </button>
              </div>
            </div>
          );
        }

        if (currentSlide.id === 'cer_consent') {
          return (
            <div className={styles.slideBody}>
              <CerConsentSlide onNext={goNext} />
            </div>
          );
        }

        if (currentSlide.id === 'cer_documents') {
          if (cerCompleted) {
            return (
              <div className={styles.slideBody}>
                <div className={styles.cerThankYou}>
                  <div className={styles.cerThankYouIcon}>✓</div>
                  <h3 className={styles.wizardTitle}>Benvenuto in CER Enpal!</h3>
                  <p>
                    La tua richiesta di adesione è stata ricevuta. Ti ricontatteremo all'indirizzo
                    indicato per completare la raccolta dei documenti.
                  </p>
                  <p>
                    Per qualsiasi domanda puoi scrivere a{' '}
                    <a href="mailto:cer@enpal.com">cer@enpal.com</a>.
                  </p>
                </div>
              </div>
            );
          }

          const isGiuridica = formData.cer_legal_type === 'persona_giuridica';
          const hasBattery = formData.cer_battery === 'yes';

          return (
            <div className={styles.slideBody}>
              <h3 className={styles.wizardTitle}>Documenti da caricare</h3>
              <p className={styles.cerDocsIntro}>
                Per completare l'adesione sarà necessario fornire i seguenti documenti:
              </p>

              <div className={styles.cerDocsList}>
                <div className={styles.cerDocsGroup}>
                  <h4>Documenti personali</h4>
                  <ul>
                    <li>Bolletta (con POD visibile)</li>
                    {isGiuridica ? (
                      <>
                        <li>Documento d'identità del proprietario</li>
                        <li>Visura camerale</li>
                      </>
                    ) : (
                      <>
                        <li>Documento d'identità</li>
                        <li>Tessera sanitaria <span className={styles.cerOptional}>(consigliata)</span></li>
                      </>
                    )}
                  </ul>
                </div>

                <div className={styles.cerDocsGroup}>
                  <h4>Documenti dell'impianto</h4>
                  <ul>
                    <li>Schema elettrico unifilare timbrato da perito abilitato</li>
                    <li>Foto/elenco matricole dei moduli fotovoltaici</li>
                    <li>Foto matricola dell'inverter installato</li>
                    <li>Verbale di allaccio</li>
                    <li>Visura catastale dell'immobile <span className={styles.cerOptional}>(facoltativa)</span></li>
                    {hasBattery && (
                      <>
                        <li>Foto contatore M2</li>
                        <li>Foto matricole delle singole batterie <span className={styles.cerOptional}>(se presente)</span></li>
                      </>
                    )}
                  </ul>
                </div>
              </div>

              <p className={styles.cerDocsNote}>
                Un nostro operatore ti contatterà per guidarti nel caricamento dei documenti. Puoi
                anche scrivere a <a href="mailto:cer@enpal.com">cer@enpal.com</a>.
              </p>

              <div className={styles.buttonGroup}>
                <button
                  type="button"
                  className={styles.btnNext}
                  onClick={() => setCerCompleted(true)}
                >
                  Ho capito — Conferma adesione
                </button>
              </div>
            </div>
          );
        }

        return (
          <div className={styles.slideBody}>
            {currentSlide.title && <h3 className={styles.wizardTitle}>{currentSlide.title}</h3>}
            {currentSlide.html && (
              <div dangerouslySetInnerHTML={{ __html: currentSlide.html }} />
            )}
          </div>
        );
      }

      default:
        return null;
    }
  };

  return (
    <section className={styles.sliderSection}>
      <div className={styles.sliderContainer}>
        <div className={styles.sliderWrapper}>
          <ProgressBar progress={progress} />

          <div className={styles.wizSlide}>
            {renderSlideContent()}
          </div>
        </div>
      </div>
    </section>
  );
};

export default SliderVpp;
